/**
 * Human-readable copy for campaign UGC guidance `textCode`s.
 *
 * The gateway's `ugc-guidance` endpoint returns machine codes (e.g. `ENC_USE_PRODUCT`,
 * `AVD_CHILDREN`) plus a type (`ENCOURAGE` / `AVOID`) and optional example imagery.
 * This map mirrors the influenster-v4-mobile `moodboard` copy so the web renders the
 * same do's and don'ts. Unknown codes fall back to a readable version of the code.
 *
 * NOTE: this is a manual port of v4-mobile `src/intl/locales/en/moodboard.json`.
 * If the mobile copy changes, re-sync from that file to avoid drift.
 */
export const GUIDANCE_COPY: Record<string, string> = {
  // ── ENCOURAGE (do's) ──────────────────────────────────────────────────────
  ENC_USE_PRODUCT: 'Show yourself using the product',
  ENC_WEAR_PRODUCT: 'Show yourself wearing the product',
  ENC_TRY_PRODUCT: 'Show yourself trying the product',
  ENC_PRODUCT_ACTION: 'Show the product performing its primary function',
  ENC_PET_PRODUCT: 'Show your pet with the product',
  ENC_PRODUCT_FIT: 'Show how the product fits',
  ENC_UNBOX_PRODUCT: 'Show the unboxing of the product',
  ENC_PRODUCT_USE_PLACE: "Show the product where you'd use it",
  ENC_BRAND_LOGO: 'Show the logo on the product',
  ENC_PRODUCT_DETAILS: 'Show the product up close to see details',
  ENC_PRODUCT_FEATURES: "Show the product's important features",
  ENC_BEFORE_AFTER: 'Take before-and-after pics',
  ENC_WELL_LIT: 'Show the product in a well-lit space',
  ENC_PRODUCT_MEAL: 'Show the product as part of a meal',
  ENC_PREPARE_PRODUCT: 'Show us how you prepare the product',
  ENC_PRODUCT_SETUP: 'Show the product setup in your home',
  ENC_INSTALL_PRODUCT: 'Show yourself installing the product at home',
  ENC_PRODUCT_APPLIED: "Show the product after it's been applied",
  ENC_PRODUCTS_TOGETHER: 'Show the products being used together',
  ENC_FULL_LOOK: 'Show us a full look using the product',
  ENC_FLATLAY_PHOTO: 'Create a flatlay photo with the product',
  ENC_VIDEO_SHOWCASE: 'Shoot a video showcasing the product',
  ENC_SELF_WITH_PRODUCT: 'Show yourself with the product',
  ENC_CHILD_WITH_PRODUCT: 'Show your child with the product',
  ENC_PRODUCT_RESULTS: "Show the results of the product after it's used",
  ENC_MALE_USING_PRODUCT: 'Show a male in your life using the product',
  ENC_MALE_WITH_PRODUCT: 'Show a male in your life with the product',
  // ── AVOID (don'ts) ────────────────────────────────────────────────────────
  AVD_SELFIE: 'Do not include yourself or a person in the shot',
  AVD_CHILDREN: 'Do not show children in the shot',
  AVD_OTHER_LOGOS: 'Do not include logos from other brands',
  AVD_ILLEGAL_DRINKING: "Don't shoot content in an area where drinking is illegal",
  AVD_UNDER_21: 'Avoid showing a person under 21',
  AVD_BOXED_PRODUCT: 'Do not show the product still in its box',
  AVD_MUSIC: 'Do not include music in your post',
  AVD_SCREENSHOT: 'Do not post a screenshot. Create your own content',
};

/** Copy for a guidance textCode, falling back to a readable version of the code. */
export function getGuidanceCopy(textCode: string): string {
  if (Object.prototype.hasOwnProperty.call(GUIDANCE_COPY, textCode)) return GUIDANCE_COPY[textCode];
  // Fallback: drop the ENC_/AVD_ prefix and title-case the remainder.
  return textCode
    .replace(/^(ENC|AVD)_/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
